<template>
  <div class="width-100">
    <h2 class="section-title">{{ $t('printAnalyses.title') }}</h2>

    <div class="form-block work-planning-form">
      <ValidationObserver
        ref="printAnalysesForm"
        v-slot="{ handleSubmit, invalid }"
      >
        <el-form
          class="operations-search-form"
          size="small"
          label-position="top"
        >
          <el-row
            :gutter="20"
            type="flex"
          >
            <el-col :span="8">
              <el-form-item>
                <span slot="label">{{
                  $t('printAnalyses.customerNameFrom')
                }}</span>
                <el-autocomplete
                  class="width-100"
                  v-model="formData.customerNameFrom"
                  value-key="label"
                  clearable
                  :fetch-suggestions="
                    (text, cb) => {
                      searchCustomer({ text, cb, field: 'name', exact: false });
                    }
                  "
                  :trigger-on-focus="false"
                  @select="(item) => selectCustomer(item, 'from')"
                  @change="(item) => emptySelectedCustomer(item, 'from')"
                >
                  <template slot-scope="{ item }">
                    <AutocompleteItem :item="item" />
                  </template>
                </el-autocomplete>
              </el-form-item>
            </el-col>

            <el-col :span="4">
              <el-form-item>
                <span slot="label">{{
                  $t('printAnalyses.customerCodeFrom')
                }}</span>
                <el-autocomplete
                  class="width-100"
                  v-model="formData.customerCodeFrom"
                  value-key="label"
                  clearable
                  :fetch-suggestions="
                    (text, cb) => {
                      searchCustomer({ text, cb, field: 'id', exact: true });
                    }
                  "
                  :trigger-on-focus="false"
                  @select="(item) => selectCustomer(item, 'from')"
                  @change="(item) => emptySelectedCustomer(item, 'from')"
                >
                  <template slot-scope="{ item }">
                    <AutocompleteItem :item="item" />
                  </template>
                </el-autocomplete>
              </el-form-item>
            </el-col>

            <el-col :span="8">
              <el-form-item>
                <span slot="label">{{
                  $t('printAnalyses.customerNameTo')
                }}</span>
                <el-autocomplete
                  class="width-100"
                  v-model="formData.customerNameTo"
                  value-key="label"
                  clearable
                  :fetch-suggestions="
                    (text, cb) => {
                      searchCustomer({ text, cb, field: 'name', exact: false });
                    }
                  "
                  :trigger-on-focus="false"
                  @select="(item) => selectCustomer(item, 'to')"
                  @change="(item) => emptySelectedCustomer(item, 'to')"
                >
                  <template slot-scope="{ item }">
                    <AutocompleteItem :item="item" />
                  </template>
                </el-autocomplete>
              </el-form-item>
            </el-col>

            <el-col :span="4">
              <el-form-item>
                <span slot="label">{{
                  $t('printAnalyses.customerCodeTo')
                }}</span>
                <el-autocomplete
                  class="width-100"
                  v-model="formData.customerCodeTo"
                  value-key="label"
                  clearable
                  :fetch-suggestions="
                    (text, cb) => {
                      searchCustomer({ text, cb, field: 'id', exact: true });
                    }
                  "
                  :trigger-on-focus="false"
                  @select="(item) => selectCustomer(item, 'to')"
                  @change="(item) => emptySelectedCustomer(item, 'to')"
                >
                  <template slot-scope="{ item }">
                    <AutocompleteItem :item="item" />
                  </template>
                </el-autocomplete>
              </el-form-item>
            </el-col>
          </el-row>

          <el-row
            :gutter="20"
            type="flex"
          >
            <el-col :span="4">
              <FormItem
                rules="required"
                :label="$t('printAnalyses.acceptedFrom')"
              >
                <el-date-picker
                  v-model="formData.acceptedFrom"
                  @change="updateStoredForm"
                  format="dd-MM-yyyy"
                  value-format="yyyy-MM-dd"
                  type="date"
                  placeholder=""
                />
              </FormItem>
            </el-col>

            <el-col :span="4">
              <FormItem
                rules="required"
                :label="$t('printAnalyses.acceptedTo')"
              >
                <el-date-picker
                  v-model="formData.acceptedTo"
                  @change="updateStoredForm"
                  format="dd-MM-yyyy"
                  value-format="yyyy-MM-dd"
                  type="date"
                  placeholder=""
                />
              </FormItem>
            </el-col>

            <el-col :span="4">
              <FormItem :label="$t('printAnalyses.analysisEndFrom')">
                <el-date-picker
                  v-model="formData.analysisEndFrom"
                  @change="updateStoredForm"
                  format="dd-MM-yyyy"
                  value-format="yyyy-MM-dd"
                  type="date"
                  placeholder=""
                />
              </FormItem>
            </el-col>

            <el-col :span="4">
              <FormItem :label="$t('printAnalyses.analysisEndTo')">
                <el-date-picker
                  v-model="formData.analysisEndTo"
                  @change="updateStoredForm"
                  format="dd-MM-yyyy"
                  value-format="yyyy-MM-dd"
                  type="date"
                  placeholder=""
                />
              </FormItem>
            </el-col>

            <el-col :span="4">
              <FormItem :label="$t('printAnalyses.analysisDueFrom')">
                <el-date-picker
                  v-model="formData.analysisDueFrom"
                  @change="updateStoredForm"
                  format="dd-MM-yyyy"
                  value-format="yyyy-MM-dd"
                  type="date"
                  placeholder=""
                />
              </FormItem>
            </el-col>

            <el-col :span="4">
              <FormItem :label="$t('printAnalyses.analysisDueTo')">
                <el-date-picker
                  v-model="formData.analysisDueTo"
                  @change="updateStoredForm"
                  format="dd-MM-yyyy"
                  value-format="yyyy-MM-dd"
                  type="date"
                  placeholder=""
                />
              </FormItem>
            </el-col>
          </el-row>

          <el-row
            :gutter="20"
            type="flex"
          >
            <el-col :span="4">
              <FormItem :label="$t('printAnalyses.commodityCode')">
                <el-select
                  v-model="formData.commodityCode"
                  placeholder=""
                  @change="updateStoredForm"
                  clearable
                >
                  <el-option
                    v-for="laboratory in laboratories"
                    :key="laboratory"
                    :value="laboratory"
                    :label="$t(`constants.${laboratory}`)"
                  />
                </el-select>
              </FormItem>
            </el-col>

            <el-col :span="4">
              <FormItem :label="$t('printAnalyses.sampleCodeFrom')">
                <el-autocomplete
                  class="width-100"
                  v-model="formData.sampleReportCodeFrom"
                  value-key="label"
                  clearable
                  placeholder="Digita almeno 3 caratteri..."
                  :fetch-suggestions="searchSampleReportCode"
                  :trigger-on-focus="false"
                  @change="updateStoredForm"
                >
                  <template slot-scope="{ item }">
                    <AutocompleteItem :item="item" />
                  </template>
                </el-autocomplete>
              </FormItem>
            </el-col>

            <el-col :span="4">
              <FormItem :label="$t('printAnalyses.sampleCodeTo')">
                <el-autocomplete
                  class="width-100"
                  v-model="formData.sampleReportCodeTo"
                  value-key="label"
                  clearable
                  placeholder="Digita almeno 3 caratteri..."
                  :fetch-suggestions="searchSampleReportCode"
                  :trigger-on-focus="false"
                  @change="updateStoredForm"
                >
                  <template slot-scope="{ item }">
                    <AutocompleteItem :item="item" />
                  </template>
                </el-autocomplete>
              </FormItem>
            </el-col>

            <el-col :span="4">
              <FormItem :label="$t('printAnalyses.analysisCode')">
                <el-autocomplete
                  v-model="analysisCodeBuffer"
                  value-key="label"
                  clearable
                  :fetch-suggestions="
                    (text, cb) => {
                      searchAnalyses({ text, cb, field: 'code' });
                    }
                  "
                  :trigger-on-focus="false"
                  @select="selectAnalysis"
                >
                  <template slot-scope="{ item }">
                    <AutocompleteAnalysis
                      :item="item"
                      :showIcon="false"
                    />
                  </template>
                </el-autocomplete>
                <FilterTags
                  :filter-data="selectedAnalysisCodes"
                  :filter="{ name: 'analysisCodes' }"
                  @tagClosed="
                    ({ item }) => {
                      removeAnalysisCode(item);
                    }
                  "
                />
              </FormItem>
            </el-col>

            <el-col :span="8">
              <FormItem :label="$t('printAnalyses.analysisDescription')">
                <el-autocomplete
                  v-model="analysisDescriptionBuffer"
                  value-key="label"
                  clearable
                  :fetch-suggestions="
                    (text, cb) => {
                      searchAnalyses({ text, cb, field: 'description' });
                    }
                  "
                  :trigger-on-focus="false"
                  @select="selectAnalysis"
                >
                  <template slot-scope="{ item }">
                    <AutocompleteAnalysis :item="item" />
                  </template>
                </el-autocomplete>
                <FilterTags
                  :filter-data="selectedAnalysisDescriptions"
                  :filter="{ name: 'analysisDescriptions' }"
                  @tagClosed="
                    ({ item }) => {
                      removeAnalysisCode(item);
                    }
                  "
                />
              </FormItem>
            </el-col>
          </el-row>

          <el-row
            :gutter="20"
            type="flex"
          >
            <el-col :span="4">
              <FormItem :label="$t('printAnalyses.methodDescription')">
                <el-autocomplete
                  v-model="formData.methodDescription"
                  value-key="label"
                  clearable
                  :fetch-suggestions="
                    (text, cb) => {
                      searchMethod(text, cb);
                    }
                  "
                  :trigger-on-focus="false"
                  @select="addMethodData"
                >
                  <template slot-scope="{ item }">
                    <AutocompleteItem :item="item" />
                  </template>
                </el-autocomplete>
                <FilterTags
                  :filterData="formData.methodTags"
                  :filter="{ name: 'method' }"
                  @tagClosed="
                    ({ item }) => {
                      removeMethodData(item);
                    }
                  "
                />
              </FormItem>
            </el-col>

            <!--
            <el-col :span="4">
              <FormItem :label="$t('printAnalyses.categoryDescription')">
                <el-input v-model="formData.categoryDescription" @change="updateStoredForm" />
              </FormItem>
            </el-col>
            -->

            <el-col :span="4">
              <FormItem :label="$t('printAnalyses.sampleStatus')">
                <el-select
                  placeholder=""
                  value=""
                  @change="addStatus"
                  clearable
                >
                  <el-option
                    v-for="status in LAB_SAMPLE_STATUSES"
                    :key="status"
                    :value="status"
                    :label="
                      $t(
                        `enums.sampleStatuses.${LAB_SAMPLE_STATUSES_DESC[status]}`
                      )
                    "
                  />
                </el-select>
                <FilterTags
                  :filter-data="selectedAnalysesStatuses"
                  :filter="{ name: 'labSampleStatus' }"
                  @tagClosed="
                    ({ item }) => {
                      removeStatus(item);
                    }
                  "
                />
              </FormItem>
            </el-col>

            <el-col :span="4">
              <FormItem :label="$t('printAnalyses.orderId')">
                <el-input
                  v-model="formData.orderId"
                  type="number"
                  @change="
                    (value) => {
                      addOrderIdData({
                        key: Number(value),
                        label: Number(value),
                      });
                    }
                  "
                />
                <FilterTags
                  :filterData="formData.orderIdsTags"
                  :filter="{ name: 'orderId' }"
                  @tagClosed="
                    ({ item }) => {
                      removeOrderIdData(item);
                    }
                  "
                />
              </FormItem>
            </el-col>

            <el-col :span="4">
              <FormItem :label="$t('printAnalyses.instrument')">
                <el-autocomplete
                  v-model="formData.analysisBrandsDescriptions"
                  value-key="label"
                  clearable
                  :fetch-suggestions="
                    (text, cb) => {
                      searchBrand(text, cb);
                    }
                  "
                  :trigger-on-focus="false"
                  @select="addAnalysisBrand"
                >
                  <template slot-scope="{ item }">
                    <AutocompleteItem :item="item" />
                  </template>
                </el-autocomplete>
                <FilterTags
                  :filterData="formData.analysisBrandsTags"
                  :filter="{ name: 'analysisBrands' }"
                  @tagClosed="
                    ({ item }) => {
                      removeAnalysisBrand(item);
                    }
                  "
                />
              </FormItem>
            </el-col>

            <el-col :span="8">
              <el-form-item>
                <span slot="label">{{
                  $t('printAnalyses.finalCustomerName')
                }}</span>
                <el-autocomplete
                  class="width-100"
                  v-model="formData.finalCustomer"
                  value-key="label"
                  clearable
                  :fetch-suggestions="
                    (text, cb) => {
                      searchCustomer({ text, cb, field: 'name', exact: false });
                    }
                  "
                  :trigger-on-focus="false"
                  @select="(item) => selectCustomer(item, 'final')"
                  @change="(item) => emptySelectedCustomer(item, 'final')"
                >
                  <template slot-scope="{ item }">
                    <AutocompleteItem :item="item" />
                  </template>
                </el-autocomplete>
              </el-form-item>
            </el-col>
          </el-row>

          <el-row
            :gutter="20"
            type="flex"
          >
            <el-col :span="4">
              <FormItem :label="$t('printAnalyses.groupingId')">
                <el-input
                  v-model.number="formData.groupingId"
                  type="number"
                  @change="
                    (value) => {
                      addGroupingIdData({
                        key: Number(value),
                        label: Number(value),
                      });
                    }
                  "
                />
                <FilterTags
                  :filterData="formData.groupingIdsTags"
                  :filter="{ name: 'groupingId' }"
                  @tagClosed="
                    ({ item }) => {
                      removeGroupingIdData(item);
                    }
                  "
                />
              </FormItem>
            </el-col>

            <el-col :span="8">
              <FormItem :label="$t('printAnalyses.location')">
                <el-autocomplete
                  v-model="formData.locationName"
                  style="width: 100%"
                  :fetch-suggestions="locationsList"
                  :trigger-on-focus="false"
                  value-key="label"
                  clearable
                  placeholder=""
                  @select="
                    (item) => {
                      formData.locationId = item.value;
                      formData.locationName = item.label;
                    }
                  "
                >
                  <template slot-scope="{ item }">
                    <AutocompleteItem :item="item" />
                  </template>
                </el-autocomplete>
              </FormItem>
            </el-col>

            <el-col :span="4">
              <FormItem :label="$t('printAnalyses.accreditedPharma')">
                <el-select
                  v-model="formData.accreditedPharma"
                  placeholder=""
                  @change="updateStoredForm"
                  clearable
                >
                  <el-option
                    v-for="item in accreditedPharmaList"
                    :key="item.value"
                    :value="item.value"
                    :label="item.label"
                  />
                </el-select>
              </FormItem>
            </el-col>

            <el-col :span="4">
              <FormItem :label="$t('printAnalyses.signedAtFrom')">
                <el-date-picker
                  v-model="formData.signedAtFrom"
                  @change="updateStoredForm"
                  format="dd-MM-yyyy"
                  value-format="yyyy-MM-dd"
                  type="date"
                  placeholder=""
                />
              </FormItem>
            </el-col>

            <el-col :span="4">
              <FormItem :label="$t('printAnalyses.signedAtTo')">
                <el-date-picker
                  v-model="formData.signedAtTo"
                  @change="updateStoredForm"
                  format="dd-MM-yyyy"
                  value-format="yyyy-MM-dd"
                  type="date"
                  placeholder=""
                />
              </FormItem>
            </el-col>
          </el-row>

          <el-row
            :gutter="20"
            type="flex"
          >
            <el-col :span="4">
              <FormItem :label="$t('printAnalyses.expandClassesLabel')">
                <el-checkbox
                  v-model="formData.expandClasses"
                  :label="$t('printAnalyses.expandClasses')"
                />
              </FormItem>
            </el-col>

            <el-col :span="11">
              <FormItem :label="$t('printAnalyses.excludePrepaidOffersLabel')">
                <el-radio-group
                  v-model="formData.prepaidOffers"
                  class="print-analyses-form__radio-group"
                  @change="updateStoredForm"
                >
                  <el-radio
                    class="print-analyses-form__radio"
                    label="both"
                  >
                    {{ $t('printAnalyses.allPrepaidOffers') }}
                  </el-radio>
                  <el-radio
                    class="print-analyses-form__radio"
                    label="exclude"
                  >
                    {{ $t('printAnalyses.excludePrepaidOffers') }}
                  </el-radio>
                  <el-radio
                    class="print-analyses-form__radio"
                    label="only"
                  >
                    {{ $t('printAnalyses.onlyPrepaidOffers') }}
                  </el-radio>
                </el-radio-group>
              </FormItem>
            </el-col>

            <el-col :span="5">
              <FormItem
                rules="required"
                :label="$t('printAnalyses.printType')"
              >
                <el-radio-group
                  v-model="formData.printType"
                  @change="updateStoredForm"
                >
                  <el-radio
                    class="print-analyses-form__radio"
                    v-for="type in printTypes"
                    :key="type.value"
                    :label="type.value"
                    >{{ type.label }}</el-radio
                  >
                </el-radio-group>
              </FormItem>
            </el-col>

            <el-col :span="4">
              <FormItem
                rules="required"
                :label="$t('printAnalyses.printMode')"
              >
                <el-radio-group
                  v-model="formData.printMode"
                  @change="updateStoredForm"
                >
                  <el-radio
                    class="print-analyses-form__radio"
                    v-for="type in printModes"
                    :key="type.value"
                    :label="type.value"
                    >{{ type.label }}</el-radio
                  >
                </el-radio-group>
              </FormItem>
            </el-col>
          </el-row>

          <FormButtons>
            <el-button
              type="primary"
              native-type="submit"
              :disabled="invalid"
              @click.prevent="handleSubmit(printAnalyses)"
            >
              {{ $t('printAnalyses.print') }}
            </el-button>
          </FormButtons>
        </el-form>
      </ValidationObserver>
    </div>
  </div>
</template>

<script>
  import moment from 'moment';
  import fileSaver from 'file-saver';
  import { mapGetters, mapActions } from 'vuex';
  import FormItem from '@/components/FormItem.vue';
  import FormButtons from '@/components/FormButtons.vue';
  import AutocompleteAnalysis from '@/components/AutocompleteAnalysis.vue';
  import {
    laboratories,
    ANALYSES_COMMODITY_CATEGORIES,
    LAB_SAMPLE_STATUSES,
    LAB_SAMPLE_STATUSES_DESC,
  } from '@/config/constants';
  import FilterTags from '@/components/FilterTags.vue';
  import AutocompleteItem from '@/components/AutocompleteItem.vue';
  import { samplesApi } from '@/api';
  import analysisFilterSuggestionsMixin from '@/mixins/analysisFilterSuggestionsMixin';
  import pick from 'lodash.pick';
  import pickBy from 'lodash.pickby';

  const initialFormData = {
    customerNameFrom: '',
    customerCodeFrom: '',
    customerFromId: null,
    customerNameTo: '',
    customerCodeTo: '',
    customerToId: null,
    acceptedFrom: '',
    acceptedTo: '',
    analysisEndFrom: '',
    analysisEndTo: '',
    analysisDueFrom: '',
    analysisDueTo: '',
    commodityCode: null,
    sampleReportCodeFrom: '',
    sampleReportCodeTo: '',
    analysisCodesDescriptions: [],
    analysisCodes: [],
    labSampleStatus: [],
    analysisBrands: [],
    analysisBrandsDescriptions: '',
    analysisBrandsTags: [],
    method: [],
    methodDescription: '',
    methodTags: [],
    expandClasses: false,
    printType: null,
    orderId: '',
    orderIds: [],
    orderIdsTags: [],
    printMode: '',
    // TBD
    // categoryDescription: null,
    finalCustomer: '',
    groupingId: null,
    groupingIds: [],
    groupingIdsTags: [],
    prepaidOffers: 'both',
    accreditedPharma: null,
    locationId: null,
    locationName: '',
    signedAtFrom: '',
    signedAtTo: '',
  };

  const apiObjectKeys = [
    'acceptedFrom',
    'acceptedTo',
    'customerToId',
    'customerFromId',
    'analysisEndFrom',
    'analysisEndTo',
    'commodityCode',
    'analysisCodes',
    'sampleReportCodeFrom',
    'sampleReportCodeTo',
    'method',
    'labSampleStatus',
    'orderIds',
    'analysisDueFrom',
    'analysisDueTo',
    'analysisBrands',
    'expandClasses',
    'prepaidOffers',
    'finalCustomer',
    'groupingIds',
    'accreditedPharma',
    'locationId',
    'signedAtFrom',
    'signedAtTo',
  ];
  const apiObjectDatesStart = [
    'acceptedFrom',
    'analysisEndFrom',
    'analysisDueFrom',
    'signedAtFrom',
  ];
  const apiObjectDatesEnd = [
    'acceptedTo',
    'analysisEndTo',
    'analysisDueTo',
    'signedAtTo',
  ];

  export default {
    name: 'PrintAnalyses',
    mixins: [analysisFilterSuggestionsMixin],
    components: {
      FormItem,
      FormButtons,
      AutocompleteAnalysis,
      AutocompleteItem,
      FilterTags,
    },
    data() {
      return {
        formData: { ...initialFormData },
        analysisCodeBuffer: '',
        analysisDescriptionBuffer: '',
        printTypes: [
          { label: this.$t('printAnalyses.briefPrint'), value: 'short' },
          { label: this.$t('printAnalyses.detailedPrint'), value: 'detailed' },
        ],
        printModes: [
          {
            label: this.$t('printAnalyses.pdfPrint'),
            value: 'analysisPrintPdf',
          },
          {
            label: this.$t('printAnalyses.excelPrint'),
            value: 'analysisPrintExcel',
          },
        ],
        analysisComodityCategories: [
          {
            label: this.$t('enums.comodityCategory.microbiological'),
            value: ANALYSES_COMMODITY_CATEGORIES.MICROBIOLOGICAL,
          },
          {
            label: this.$t('enums.comodityCategory.chemical'),
            value: ANALYSES_COMMODITY_CATEGORIES.CHEMICAL,
          },
        ],
        laboratories,
        LAB_SAMPLE_STATUSES,
        LAB_SAMPLE_STATUSES_DESC,
        accreditedPharmaList: [
          {
            label: this.$t('printAnalyses.noAccredited'),
            value: 'no accredited',
          },
          { label: this.$t('printAnalyses.accredited'), value: 'accredited' },
          { label: this.$t('printAnalyses.pharma'), value: 'pharma' },
        ],
      };
    },
    computed: {
      ...mapGetters(['forms']),
      selectedAnalysesStatuses() {
        return this.formData.labSampleStatus.map((labSampleStatus) => ({
          label: this.$t(
            `enums.sampleStatuses.${LAB_SAMPLE_STATUSES_DESC[labSampleStatus]}`
          ),
          key: labSampleStatus,
        }));
      },
      selectedAnalysisCodes() {
        return this.formData.analysisCodesDescriptions.map((item, index) => ({
          label: item.code,
          key: index,
        }));
      },
      selectedAnalysisDescriptions() {
        return this.formData.analysisCodesDescriptions.map((item, index) => ({
          label: item.description,
          key: index,
        }));
      },
    },
    methods: {
      ...mapActions(['updateForm']),
      async searchSampleReportCode(text, cb) {
        // Require minimum 3 characters to prevent overly broad searches
        if (!text || text.trim().length < 3) {
          cb([
            {
              label: 'Digita almeno 3 caratteri per iniziare la ricerca',
              value: null,
              disabled: true,
            },
          ]);
          return;
        }
        const customersResponse = await samplesApi.autocompleteFetch(
          'sampleReportCode',
          text,
          false
        );
        const filteredResults = customersResponse.data.map((c) => ({
          label: c.sampleReportCode,
          value: c.sampleReportCode,
        }));
        cb(filteredResults);
      },
      selectAnalysis(item) {
        if (
          this.formData.analysisCodesDescriptions.filter(
            (current) => current.value === item.value
          ).length === 0
        ) {
          this.formData.analysisCodesDescriptions.push(item);
        }
      },
      removeAnalysisCode(item) {
        this.formData.analysisCodesDescriptions.splice(item.key, 1);
      },
      selectCustomer(item, field) {
        if (field === 'from') {
          this.formData.customerFromId = item.value;
          this.formData.customerNameFrom = item.customerName;
          this.formData.customerCodeFrom = item.code;
        }
        if (field === 'to') {
          this.formData.customerToId = item.value;
          this.formData.customerNameTo = item.customerName;
          this.formData.customerCodeTo = item.code;
        }
        if (field === 'final') {
          this.formData.finalCustomer = item.customerName;
        }
        this.updateForm({ formKey: 'printAnalyses', formData: this.formData });
      },
      emptySelectedCustomer(text, field) {
        if (text === '' && field === 'to') {
          this.formData.customerToId = '';
          this.formData.customerNameTo = '';
          this.formData.customerCodeTo = '';
          this.updateForm({
            formKey: 'printAnalyses',
            formData: this.formData,
          });
        }
        if (text === '' && field === 'from') {
          this.formData.customerFromId = '';
          this.formData.customerNameFrom = '';
          this.formData.customerCodeFrom = '';
          this.updateForm({
            formKey: 'printAnalyses',
            formData: this.formData,
          });
        }
        if (text === '' && field === 'final') {
          this.formData.finalCustomer = '';
          this.updateForm({
            formKey: 'printAnalyses',
            formData: this.formData,
          });
        }
      },
      addStatus(item) {
        const existing = this.formData.labSampleStatus.indexOf(item);
        if (existing < 0) {
          this.formData.labSampleStatus.push(item);
        }
      },
      removeStatus(item) {
        const indexToRemove = this.formData.labSampleStatus.indexOf(item.key);
        if (indexToRemove >= 0) {
          this.formData.labSampleStatus.splice(indexToRemove, 1);
        }
      },
      updateStoredForm() {
        this.updateForm({ formKey: 'printAnalyses', formData: this.formData });
      },
      addMethodData(item) {
        if (!this.formData.method.includes(item.label)) {
          this.formData.method.push(item.label);
          this.formData.methodTags.push(item);
        }
        this.formData.methodDescription = '';
      },
      removeMethodData(item) {
        this.formData.method = this.formData.method.filter(
          (arrayItem) => arrayItem !== item.label
        );
        this.formData.methodTags = this.formData.methodTags.filter(
          (arrayItem) => arrayItem.key !== item.key
        );
      },
      addOrderIdData(item) {
        if (!this.formData.orderIds.includes(item.label)) {
          this.formData.orderIds.push(item.label);
          this.formData.orderIdsTags.push(item);
        }
        this.formData.orderId = '';
      },
      removeOrderIdData(item) {
        this.formData.orderIds = this.formData.orderIds.filter(
          (arrayItem) => arrayItem !== item.label
        );
        this.formData.orderIdsTags = this.formData.orderIdsTags.filter(
          (arrayItem) => arrayItem.key !== item.key
        );
      },
      async printAnalyses() {
        const loading = this.$loading({
          lock: true,
          background: 'rgba(48,53,82,0.9)',
        });
        const data = this.prepareFormData();
        try {
          const response = await samplesApi[this.formData.printMode](
            this.formData.printType,
            data
          );
          if (this.formData.printMode === 'analysisPrintPdf') {
            fileSaver.saveAs(response.data, 'analysis-print.pdf');
          } else {
            fileSaver.saveAs(response.data, 'analysis-print.xlsx');
          }
          loading.close();
        } catch (error) {
          loading.close();
          // console.log(error.response.data);
          this.$notify({ showClose: true, type: 'error', message: 'Error' });
        }
      },
      prepareFormData() {
        this.formData.analysisCodes =
          this.formData.analysisCodesDescriptions.map((item) => item.code);
        const data = pickBy(
          pick(this.formData, apiObjectKeys) // pick only requested values
        ); // remove falsy values

        // set false values
        data.expandClasses = data.expandClasses ?? false;

        Object.entries(data).forEach(([key, value]) => {
          if (apiObjectDatesStart.includes(key)) {
            data[key] = moment.utc(value).startOf('day').toDate();
          }
          if (apiObjectDatesEnd.includes(key)) {
            data[key] = moment.utc(value).endOf('day').toDate();
          }
        });
        return data;
      },
      addGroupingIdData(item) {
        if (!this.formData.groupingIds.includes(item.label)) {
          this.formData.groupingIds.push(item.label);
          this.formData.groupingIdsTags.push(item);
        }
        this.formData.groupingId = null;
      },
      removeGroupingIdData(item) {
        this.formData.groupingIds = this.formData.groupingIds.filter(
          (arrayItem) => arrayItem !== item.label
        );
        this.formData.groupingIdsTags = this.formData.groupingIdsTags.filter(
          (arrayItem) => arrayItem.key !== item.key
        );
      },
      addAnalysisBrand(item) {
        if (!this.formData.analysisBrands.includes(item.label)) {
          this.formData.analysisBrands.push(item.label);
          this.formData.analysisBrandsTags.push(item);
        }
        this.formData.analysisBrandsDescriptions = item.label;
        this.updateForm({
          formKey: 'printAnalyses',
          formData: this.formData,
        });
      },
      removeAnalysisBrand(item) {
        this.formData.analysisBrands = this.formData.analysisBrands.filter(
          (arrayItem) => arrayItem !== item.label
        );
        this.formData.analysisBrandsTags =
          this.formData.analysisBrandsTags.filter(
            (arrayItem) => arrayItem.key !== item.key
          );
        this.updateForm({
          formKey: 'printAnalyses',
          formData: this.formData,
        });
      },
    },
    created() {
      this.formData = Object.keys(this.forms.printAnalyses).length
        ? { ...this.forms.printAnalyses }
        : { ...initialFormData };
    },
  };
</script>
<style lang="scss" scoped>
  .el-autocomplete {
    width: 100%;
  }

  .print-analyses-form__radio-group {
    display: flex;
    flex-direction: column;
  }
</style>
